import React, { useCallback, useEffect, useRef, useState } from 'react';
import bannerImg from '../assets/anniversary-banner.jpg';
import badgeImg from '../assets/anniversary-10-years.png';
import './AnniversaryIntro.css';

const STORAGE_KEY = 'tissa-10-years-intro-seen';
const SHOW_MS = 4000;
const FLY_MS = 900;

function hasSeenIntro() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function markIntroSeen() {
  try {
    sessionStorage.setItem(STORAGE_KEY, '1');
  } catch {
    /* storage unavailable */
  }
}

// phases: 'init' -> 'banner' -> 'flying' -> 'badge' (or 'hidden')
export default function AnniversaryIntro() {
  const [phase, setPhase] = useState('init');
  const [flyStyle, setFlyStyle] = useState(null);
  const cardRef = useRef(null);
  const badgeRef = useRef(null);
  const flyTimer = useRef(null);

  useEffect(() => {
    // Never bake the overlay into react-snap prerendered HTML.
    if (/ReactSnap/i.test(navigator.userAgent)) {
      setPhase('hidden');
      return;
    }
    setPhase(hasSeenIntro() ? 'badge' : 'banner');
  }, []);

  const closeBanner = useCallback(() => {
    markIntroSeen();
    const card = cardRef.current;
    const badge = badgeRef.current;
    if (card && badge) {
      const c = card.getBoundingClientRect();
      const b = badge.getBoundingClientRect();
      const dx = b.left + b.width / 2 - (c.left + c.width / 2);
      const dy = b.top + b.height / 2 - (c.top + c.height / 2);
      setFlyStyle({
        transform: `translate(${dx}px, ${dy}px) scale(${b.width / c.width})`,
        opacity: 0,
      });
    }
    setPhase('flying');
    flyTimer.current = setTimeout(() => setPhase('badge'), FLY_MS);
  }, []);

  useEffect(() => () => clearTimeout(flyTimer.current), []);

  useEffect(() => {
    if (phase !== 'banner') return undefined;
    const timer = setTimeout(closeBanner, SHOW_MS);
    const onKey = (e) => {
      if (e.key === 'Escape') closeBanner();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [phase, closeBanner]);

  const replay = () => {
    setFlyStyle(null);
    setPhase('banner');
  };

  if (phase === 'init' || phase === 'hidden') return null;

  const overlayOpen = phase === 'banner' || phase === 'flying';

  return (
    <>
      {overlayOpen && (
        <div
          className={`anniv-overlay ${phase === 'flying' ? 'is-flying' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label="TISSA Technology celebrates 10 years of excellence"
          style={{ '--anniv-fly-ms': `${FLY_MS}ms` }}
          onClick={phase === 'banner' ? closeBanner : undefined}
        >
          <div className="anniv-card" ref={cardRef} style={flyStyle || undefined}>
            <img src={bannerImg} alt="TISSA Technology — 10 Years of Excellence, 2016 to 2026" />
            <div className="anniv-progress" style={{ '--anniv-show-ms': `${SHOW_MS}ms` }} aria-hidden="true" />
          </div>
          <button
            type="button"
            className="anniv-skip"
            onClick={(e) => {
              e.stopPropagation();
              closeBanner();
            }}
          >
            Skip ✕
          </button>
        </div>
      )}

      <div className={`anniv-badge-wrap ${phase === 'badge' ? 'is-visible' : ''}`} ref={badgeRef}>
        <button
          type="button"
          className="anniv-badge"
          onClick={replay}
          aria-label="TISSA Technology 10 Years of Excellence — view celebration banner"
          tabIndex={phase === 'badge' ? 0 : -1}
        >
          <span className="anniv-badge-glow" aria-hidden="true" />
          <img src={badgeImg} alt="" />
        </button>
        <button
          type="button"
          className="anniv-badge-close"
          onClick={() => setPhase('hidden')}
          aria-label="Dismiss 10 years badge"
          tabIndex={phase === 'badge' ? 0 : -1}
        >
          ✕
        </button>
      </div>
    </>
  );
}
